'use client';

import React, { useState, useRef } from 'react';
import { Image, Video, MessageSquareHeart, Type, X, Upload, Send, Link, HardDrive } from 'lucide-react';
import { api, FeedPost, PostType, ConfessionMood } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { Button } from '@/components/ui/Button';

interface CreatePostProps {
  onPost: (post: FeedPost) => void;
}

const POST_TYPES: { type: PostType; icon: React.ReactNode; label: string; color: string }[] = [
  { type: 'TEXT',       icon: <Type size={15} />,               label: 'Thought',   color: 'text-brand-lime' },
  { type: 'PHOTO',      icon: <Image size={15} />,              label: 'Photo',     color: 'text-cyan-400'   },
  { type: 'VIDEO',      icon: <Video size={15} />,              label: 'Video',     color: 'text-red-400'    },
  { type: 'CONFESSION', icon: <MessageSquareHeart size={15} />, label: 'Confess',   color: 'text-pink-400'   },
];

const MOODS: { value: ConfessionMood; emoji: string; label: string }[] = [
  { value: 'FUNNY',     emoji: '😂', label: 'Funny'     },
  { value: 'SPICY',     emoji: '🌶️', label: 'Spicy'    },
  { value: 'SAD',       emoji: '😢', label: 'Sad'       },
  { value: 'WHOLESOME', emoji: '🥰', label: 'Wholesome' },
  { value: 'RANDOM',    emoji: '🎲', label: 'Random'    },
];

const MAX_CONTENT = 500;
const MAX_IMG_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

export function CreatePost({ onPost }: CreatePostProps) {
  const { token, user } = useAuthStore();
  const { addToast } = useUIStore();

  const [activeType, setActiveType] = useState<PostType>('TEXT');
  const [content, setContent] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoMode, setVideoMode] = useState<'url' | 'local'>('url');
  const [videoPreview, setVideoPreview] = useState<string | null>(null); // object URL for preview
  const [videoData, setVideoData] = useState<string | null>(null);       // base64 for upload
  const [videoMime, setVideoMime] = useState<string>('');
  const [mood, setMood] = useState<ConfessionMood>('RANDOM');
  const [imgPreview, setImgPreview] = useState<string | null>(null);
  const [imgMime, setImgMime] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const videoFileRef = useRef<HTMLInputElement>(null);

  const displayName = user?.username || 'You';
  const charsLeft = MAX_CONTENT - content.length;
  const isOverLimit = charsLeft < 0;

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMG_BYTES) {
      addToast({ message: 'Image too large (max 5 MB)', type: 'error' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImgPreview(reader.result as string);
      setImgMime(file.type);
    };
    reader.readAsDataURL(file);
  }

  function clearImage() {
    setImgPreview(null);
    setImgMime('');
    if (fileRef.current) fileRef.current.value = '';
  }

  function clearVideo() {
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoPreview(null);
    setVideoData(null);
    setVideoMime('');
    if (videoFileRef.current) videoFileRef.current.value = '';
  }

  function resetForm() {
    setContent('');
    setVideoUrl('');
    setVideoMode('url');
    clearVideo();
    setMood('RANDOM');
    clearImage();
  }

  function handleVideoFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_VIDEO_BYTES) {
      addToast({ message: 'Video too large (max 50 MB)', type: 'error' });
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setVideoPreview(objectUrl);
    setVideoMime(file.type);
    const reader = new FileReader();
    reader.onload = () => setVideoData(reader.result as string);
    reader.readAsDataURL(file);
  }

  function canSubmit() {
    if (loading || isOverLimit) return false;
    if (activeType === 'TEXT' || activeType === 'CONFESSION') return content.trim().length > 0;
    if (activeType === 'PHOTO') return !!imgPreview;
    if (activeType === 'VIDEO') {
      if (videoMode === 'url') return videoUrl.trim().length > 0;
      return !!videoData;
    }
    return false;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit()) return;
    setLoading(true);
    try {
      const payload: Parameters<typeof api.feed.createPost>[0] = { type: activeType };
      if (activeType === 'TEXT') payload.content = content.trim();
      if (activeType === 'CONFESSION') { payload.content = content.trim(); payload.mood = mood; }
      if (activeType === 'PHOTO') {
        payload.mediaData = imgPreview!;
        payload.mediaType = imgMime;
        if (content.trim()) payload.content = content.trim();
      }
      if (activeType === 'VIDEO') {
        if (videoMode === 'url') {
          payload.videoUrl = videoUrl.trim();
        } else {
          payload.mediaData = videoData!;
          payload.mediaType = videoMime;
        }
        if (content.trim()) payload.content = content.trim();
      }

      const res = await api.feed.createPost(payload, token ?? '');
      onPost(res.post);
      resetForm();
      addToast({ message: 'Posted anonymously!', type: 'success' });
    } catch {
      addToast({ message: 'Failed to post. Try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-brand-surface/60 border border-white/[0.06] rounded-2xl overflow-hidden">
      {/* Type selector */}
      <div className="flex border-b border-white/[0.05]">
        {POST_TYPES.map(({ type, icon, label, color }) => (
          <button
            key={type}
            type="button"
            onClick={() => { setActiveType(type); resetForm(); }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-all ${
              activeType === type
                ? `${color} border-b-2 border-current bg-white/[0.03]`
                : 'text-brand-gray hover:text-brand-white hover:bg-white/[0.02]'
            }`}
          >
            {icon}
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      <div className="p-4">
        {/* Avatar row */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-brand-lime/10 border border-brand-lime/20 flex items-center justify-center text-sm shrink-0 mt-0.5">
            🎭
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-brand-gray mb-2">Posting as <span className="text-brand-white/60 font-medium">Anonymous</span></p>

            {/* TEXT */}
            {activeType === 'TEXT' && (
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="What's on your mind? Share with strangers..."
                rows={3}
                className="w-full bg-transparent text-brand-white/90 text-sm placeholder:text-brand-gray resize-none focus:outline-none leading-relaxed"
              />
            )}

            {/* CONFESSION */}
            {activeType === 'CONFESSION' && (
              <>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="I have to confess..."
                  rows={3}
                  className="w-full bg-transparent text-brand-white/90 text-sm placeholder:text-brand-gray resize-none focus:outline-none leading-relaxed italic"
                />
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {MOODS.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setMood(m.value)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                        mood === m.value
                          ? 'bg-brand-lime/20 border-brand-lime/40 text-brand-lime'
                          : 'border-white/10 text-brand-gray hover:border-white/20'
                      }`}
                    >
                      {m.emoji} {m.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* PHOTO */}
            {activeType === 'PHOTO' && (
              <>
                {imgPreview ? (
                  <div className="relative rounded-xl overflow-hidden mb-3">
                    <img src={imgPreview} alt="Preview" className="w-full max-h-64 object-cover" />
                    <button
                      type="button"
                      onClick={clearImage}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                    >
                      <X size={13} />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileRef.current?.click()}
                    className="flex flex-col items-center justify-center gap-2 h-36 border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-brand-lime/40 hover:bg-brand-lime/5 transition-all mb-3"
                  >
                    <Upload size={22} className="text-brand-gray" />
                    <p className="text-xs text-brand-gray">Click to upload photo (max 5 MB)</p>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Add a caption (optional)..."
                  rows={2}
                  className="w-full bg-transparent text-brand-white/90 text-sm placeholder:text-brand-gray resize-none focus:outline-none leading-relaxed"
                />
              </>
            )}

            {/* VIDEO */}
            {activeType === 'VIDEO' && (
              <>
                {/* URL / Local toggle */}
                <div className="flex rounded-lg bg-brand-surface-2/60 p-0.5 mb-3 w-fit">
                  <button
                    type="button"
                    onClick={() => { setVideoMode('url'); clearVideo(); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      videoMode === 'url'
                        ? 'bg-brand-lime text-brand-black'
                        : 'text-brand-gray hover:text-brand-white'
                    }`}
                  >
                    <Link size={11} /> URL
                  </button>
                  <button
                    type="button"
                    onClick={() => { setVideoMode('local'); setVideoUrl(''); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      videoMode === 'local'
                        ? 'bg-brand-lime text-brand-black'
                        : 'text-brand-gray hover:text-brand-white'
                    }`}
                  >
                    <HardDrive size={11} /> Local File
                  </button>
                </div>

                {videoMode === 'url' ? (
                  <input
                    type="url"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="Paste YouTube or video URL..."
                    className="w-full bg-brand-surface-2 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-gray focus:outline-none focus:border-brand-lime/50 mb-3"
                  />
                ) : (
                  <>
                    {videoPreview ? (
                      <div className="relative rounded-xl overflow-hidden mb-3 bg-black">
                        <video
                          src={videoPreview}
                          controls
                          muted
                          className="w-full max-h-52 object-contain"
                        />
                        <button
                          type="button"
                          onClick={clearVideo}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => videoFileRef.current?.click()}
                        className="flex flex-col items-center justify-center gap-2 h-36 border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-red-400/40 hover:bg-red-500/5 transition-all mb-3"
                      >
                        <Upload size={22} className="text-brand-gray" />
                        <p className="text-xs text-brand-gray">Click to upload video (max 50 MB)</p>
                        <p className="text-[11px] text-brand-gray/50">MP4, WebM, MOV supported</p>
                      </div>
                    )}
                    <input
                      ref={videoFileRef}
                      type="file"
                      accept="video/mp4,video/webm,video/ogg,video/quicktime"
                      onChange={handleVideoFileChange}
                      className="hidden"
                    />
                  </>
                )}

                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Add a caption (optional)..."
                  rows={2}
                  className="w-full bg-transparent text-brand-white/90 text-sm placeholder:text-brand-gray resize-none focus:outline-none leading-relaxed"
                />
              </>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.05]">
              <span className={`text-xs ${isOverLimit ? 'text-brand-red' : charsLeft < 50 ? 'text-yellow-400' : 'text-brand-gray'}`}>
                {activeType !== 'VIDEO' ? `${charsLeft} chars left` : ''}
              </span>
              <Button type="submit" size="sm" loading={loading} disabled={!canSubmit()}>
                <Send size={13} />
                Post anonymously
              </Button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
